"use client";

import Link from "next/link";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  createLumiApiClient,
  LumiApiError,
  type SubscriptionResponse,
} from "@/lib/lumi-api";

type BillingPeriod = "monthly" | "yearly";

type Status = "loading" | "loaded" | "signed-out" | "unavailable";

// Shared first-party billing panel (mirrors the desktop account plan card):
// plan badge, renewal date, Go Pro monthly/yearly checkout, Manage
// subscription (Stripe Billing Portal for existing customers), and refresh.
// Talks straight to the Rust backend via /api/v1/billing/*.
export function BillingPlanCard() {
  const client = useMemo(() => createLumiApiClient(), []);
  const [status, setStatus] = useState<Status>("loading");
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(
    null,
  );
  const [checkingOut, setCheckingOut] = useState<BillingPeriod | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadSubscription = useCallback(async () => {
    setStatus("loading");
    setError(null);

    try {
      const me = await client.me();
      if (!me.authenticated) {
        setSubscription(null);
        setStatus("signed-out");
        return;
      }

      setSubscription(await client.getSubscription());
      setStatus("loaded");
    } catch {
      setSubscription(null);
      setStatus("unavailable");
    }
  }, [client]);

  useEffect(() => {
    void loadSubscription();
  }, [loadSubscription]);

  const startCheckout = async (period: BillingPeriod) => {
    if (checkingOut) {
      return;
    }

    setCheckingOut(period);
    setError(null);

    try {
      const { url } = await client.startCheckout(period);
      window.location.href = url;
    } catch (checkoutError) {
      setCheckingOut(null);
      setError(checkoutErrorMessage(checkoutError));
    }
  };

  if (status === "loading") {
    return <p className="text-sm text-app-body">Loading your plan…</p>;
  }

  if (status === "signed-out") {
    return (
      <div>
        <p className="text-sm text-app-body">
          Sign in to manage your Lumi plan.
        </p>
        <Link
          className="mt-4 inline-flex rounded-md bg-app-text px-5 py-2.5 font-semibold text-app-bg transition-colors hover:bg-app-text/90"
          href="/sign-in"
        >
          Sign in
        </Link>
      </div>
    );
  }

  if (status === "unavailable") {
    return (
      <div>
        <p className="text-sm text-app-body">
          We couldn&apos;t load your subscription right now.
        </p>
        <Button className="mt-4" variant="secondary" onClick={loadSubscription}>
          Try again
        </Button>
      </div>
    );
  }

  const isPro = subscription?.is_pro ?? false;
  const renewsOn = subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString()
    : null;

  return (
    <div>
      <div className="flex items-center gap-3">
        <span className="text-lg font-semibold text-app-text">Plan</span>
        <span
          className={`rounded-full px-3 py-1 text-sm font-semibold ${
            isPro
              ? "bg-app-text text-app-bg"
              : "bg-app-tinted text-app-text"
          }`}
        >
          {isPro ? "Pro" : "Free"}
        </span>
      </div>

      {isPro && renewsOn && (
        <p className="mt-2 text-sm text-app-body">Renews {renewsOn}</p>
      )}

      <div className="mt-4 flex flex-wrap gap-3">
        {isPro ? (
          <Button
            disabled={checkingOut !== null}
            onClick={() => startCheckout("monthly")}
          >
            {checkingOut ? "Opening billing portal…" : "Manage subscription"}
          </Button>
        ) : (
          <>
            <Button
              disabled={checkingOut !== null}
              onClick={() => startCheckout("monthly")}
            >
              {checkingOut === "monthly" ? "Opening checkout…" : "Go Pro · monthly"}
            </Button>
            <Button
              disabled={checkingOut !== null}
              variant="secondary"
              onClick={() => startCheckout("yearly")}
            >
              {checkingOut === "yearly" ? "Opening checkout…" : "Go Pro · yearly"}
            </Button>
          </>
        )}
        <Button
          disabled={checkingOut !== null}
          variant="ghost"
          onClick={loadSubscription}
        >
          Refresh
        </Button>
      </div>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
    </div>
  );
}

function checkoutErrorMessage(error: unknown) {
  if (error instanceof LumiApiError) {
    if (error.status === 501) {
      return "Billing isn't set up on this server yet. Please try again later or contact support.";
    }
    if (error.status === 401) {
      return "Your session expired. Sign in again to manage your plan.";
    }
    if (error.status === 429) {
      return "Too many checkout attempts. Please wait a bit and try again.";
    }
    return error.message;
  }

  return "Something went wrong starting checkout. Please try again.";
}
